import React, { useMemo } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useExplainer } from "@/contexts/ui/explainer";
import { genieCapabilities } from "@/data/genie/capabilities";
import { SideDrawer } from '@/components/common/SideDrawer';

const CapabilityCard = ({
  title,
  description,
  Icon,
  onClick,
  ctaLabel,
}: {
  title: string;
  description: string;
  Icon: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  ctaLabel: string;
}) => {
  return (
    <Card className="p-4 flex items-start gap-3">
      <div className="h-10 w-10 rounded-md bg-genie-primary/10 flex items-center justify-center text-genie-primary">
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1">
        <div className="font-medium">{title}</div>
        <div className="text-sm text-muted-foreground mt-1">{description}</div>
        <Button variant="outline" size="sm" className="mt-3" onClick={onClick}>
          {ctaLabel}
        </Button>
      </div>
    </Card>
  );
};

const DemoTranscript = ({ lines }: { lines: { speaker: "Genie" | "User"; text: string }[] }) => {
  return (
    <div className="space-y-2">
      {lines.map((l, i) => (
        <div key={i} className={`text-sm ${l.speaker === "Genie" ? "text-genie-primary" : "text-foreground"}`}>
          <span className="font-medium mr-1">{l.speaker}:</span>
          <span>{l.text}</span>
        </div>
      ))}
    </div>
  );
};

export const GenieExplainerOverlay = () => {
  const { isOpen, close, open, currentView } = useExplainer();

  const shuffled = useMemo(() => {
    const arr = [...genieCapabilities];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr.slice(0, 4);
  }, [isOpen]);

  const handleCapabilityClick = (action: string | undefined, target?: string) => {
    try {
      // eslint-disable-next-line no-console
      console.log("analytics:event", { name: "capability_click", action, target });
    } catch {}
    if (action === "navigate" && target) {
      window.location.href = target;
    }
    if (action === "open_demo" && target) {
      open(target as any);
    }
  };

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={close}
      width={"900px"}
      title={"What can Genies do?"}
      branding={{ primaryColor: "#4E9CFF" }}
      actionButton={
        <Button variant="secondary" size="sm" onClick={() => window.open("/playbooks", "_self")}>Learn more</Button>
      }
    >
      <div className="py-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {shuffled.map((c) => (
            <CapabilityCard
              key={c.id}
              title={c.title}
              description={c.description}
              Icon={c.icon}
              ctaLabel={c.ctaLabel}
              onClick={() => handleCapabilityClick(c.ctaAction, c.ctaTarget)}
            />
          ))}
        </div>

        <div className="border rounded-md p-4">
          <Tabs defaultValue={currentView} value={currentView}>
            <TabsList className="mb-3">
              <TabsTrigger value="default" onClick={() => open("default")}>Overview</TabsTrigger>
              <TabsTrigger value="sales" onClick={() => open("sales")}>Sales</TabsTrigger>
              <TabsTrigger value="support" onClick={() => open("support")}>Support</TabsTrigger>
              <TabsTrigger value="booking" onClick={() => open("booking")}>Booking</TabsTrigger>
            </TabsList>
            <TabsContent value="default">
              <div className="text-sm text-muted-foreground">
                Quick, flexible assistants that answer questions, guide steps, and capture data.
              </div>
            </TabsContent>
            <TabsContent value="sales">
              <DemoTranscript
                lines={[
                  { speaker: "Genie", text: "Hi! What are you looking to achieve today?" },
                  { speaker: "User", text: "I want help choosing the right plan." },
                  { speaker: "Genie", text: "Great. What’s your team size and primary use case?" },
                  { speaker: "User", text: "8 people, client onboarding." },
                  { speaker: "Genie", text: "You’ll likely love our Growth plan. Want a demo link?" },
                ]}
              />
            </TabsContent>
            <TabsContent value="support">
              <DemoTranscript
                lines={[
                  { speaker: "Genie", text: "Tell me the issue and I’ll help troubleshoot." },
                  { speaker: "User", text: "My login is failing after password reset." },
                  { speaker: "Genie", text: "Got it. Did you confirm the reset email link?" },
                  { speaker: "User", text: "I think so…" },
                  { speaker: "Genie", text: "I’ve sent a fresh link and opened a ticket for tracking." },
                ]}
              />
            </TabsContent>
            <TabsContent value="booking">
              <DemoTranscript
                lines={[
                  { speaker: "Genie", text: "What date works best for your appointment?" },
                  { speaker: "User", text: "Next Tuesday afternoon." },
                  { speaker: "Genie", text: "2:30 PM is open. Should I book it and text you details?" },
                  { speaker: "User", text: "Yes, please." },
                  { speaker: "Genie", text: "All set! You’ll get a confirmation shortly." },
                ]}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </SideDrawer>
  );
};

export default GenieExplainerOverlay;


